import { Reference } from "./reference";

export type ScannedPageMode = "transcribe" | "attach-images";

export const DEFAULT_SCANNED_PAGE_MODE: ScannedPageMode = "transcribe";

/** Max scanned page images attached to a single analysis request. */
export const ATTACH_IMAGES_PER_REQUEST = 40;

export const SCANNED_PAGE_MODE_STORAGE_KEY = "ectd.scannedPageMode";

/** Default (cheap) vision model used to transcribe scanned PDF pages. */
export const DEFAULT_VISION_MODEL = "google/gemini-3.8-flash";

export type ContentTextPart = { type: "text"; text: string };
export type ContentImagePart = {
  type: "image_url";
  image_url: { url: string };
};
export type ContentPart = ContentTextPart | ContentImagePart;
export type MessageContent = string | ContentPart[];

export type PdfExcerptImage = {
  /** Human-readable label, e.g. "file.pdf p.4". */
  label: string;
  dataUrl: string;
};

export type PdfExcerpt = {
  fileName: string;
  text: string;
  /** Optional eCTD structural key (parent element tag), e.g. "m3-2-p-5-4-batch-analyses" or "pi-doc". */
  ectdKey?: string;
  /** Optional eCTD leaf title, e.g. "batch-analyses" or "common-spc". */
  ectdTitle?: string;
  /**
   * Optional page images to attach to the user message (attach-images mode).
   * When present, `buildUserContent` returns multimodal content parts.
   */
  images?: PdfExcerptImage[];
  /**
   * True when this excerpt already dropped content for length before
   * `buildUserContent` (e.g. per-file budget in grouped analysis).
   */
  inputTruncated?: boolean;
};

export type RunPromptOptions = {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  system?: string;
  /** Override the default BASE_PROMPT (e.g. GROUPED_BASE_PROMPT for grouped analysis). */
  basePrompt?: string;
  /** Truncate concatenated PDF text to this many characters (soft guardrail). */
  maxInputChars?: number;
  /** Optional JWT access token to call backend GraphQL for provisioning. */
  authToken?: string;
  /** How scanned pages are provided to the analysis prompt. */
  scannedPageMode?: ScannedPageMode;
};

export type RunPromptResult = {
  text: string;
  fileName: string;
  model: string;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  reference: Reference;
  raw: unknown;
  /**
   * True when the prompt sent to the model was shortened for length
   * (per-file budget and/or combined input cap).
   */
  inputTruncated?: boolean;
};

export type BuiltUserContent = {
  content: MessageContent;
  truncated: boolean;
};

export const BASE_PROMPT = `Check if data provided in those files match requirements of parts specified in the specification document. Assume each file contains only part of the specification, try to match the input document to only this part of specification. Return output in a structured format with those sections (each heading as a h3): "Errors", "Missing Data", "Warnings", "Summary". Translate the Summary to English, Hungarian and Polish. Make the whole Error section use red font when errors are present. Use a very simple HTML formatting for the output. Ommit html, head, body tags, the output will be added to already existing HTML document.`;

export const GROUPED_BASE_PROMPT = `The source document below is a set of PDF files that together form one eCTD section group (e.g. all subsections of 3.2.S.4 for one manufacturer). Each file is marked with "=== File:" and the eCTD subsection it was matched to. Check if the data provided in these files together match the requirements of the corresponding parts of the specification document. For each listed subsection, check the matching file(s) against only that part of the specification. Explicitly confirm which subsections are covered and point out subsections listed as missing. Return output in a structured format with those sections (each heading as a h3): "Errors", "Missing Data", "Warnings", "Summary". Translate the Summary to English, Hungarian and Polish. Make the whole Error section use red font when errors are present. Use a very simple HTML formatting for the output. Ommit html, head, body tags, the output will be added to already existing HTML document.`;

export const BASE_SYSTEM_PROMPT =
  "You are a meticulous expert assistant. When answering, use only the information provided in the PDF excerpts unless the user clearly asks for general knowledge. If addresses and companies names are present in the document check them with the general knowledge.";

export function readScannedPageMode(): ScannedPageMode {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_SCANNED_PAGE_MODE;
    const raw = localStorage.getItem(SCANNED_PAGE_MODE_STORAGE_KEY);
    if (raw === "attach-images" || raw === "transcribe") return raw;
  } catch (_) {
    // ignore storage errors (private mode, SSR)
  }
  return DEFAULT_SCANNED_PAGE_MODE;
}

export function writeScannedPageMode(mode: ScannedPageMode): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(SCANNED_PAGE_MODE_STORAGE_KEY, mode);
  } catch (_) {
    // ignore
  }
}

/**
 * Models known to accept image_url content parts. Used to fall back to
 * transcription when the selected analysis model cannot see images.
 */
export function isVisionCapableModel(model: string | undefined): boolean {
  if (!model) return true; // default analysis model is vision-capable
  const m = model.toLowerCase();
  return (
    m.includes("gpt-4o") ||
    m.includes("gpt-5") ||
    m.includes("gemini") ||
    m.includes("claude") ||
    m.includes("grok") ||
    m.includes("qwen") ||
    m.includes("vision") ||
    m.includes("vl-")
  );
}

export function buildUserContent(
  basePrompt: string,
  sourceDocument: PdfExcerpt,
  reference: Reference,
  maxChars: number
): BuiltUserContent {
  const header = basePrompt.trim();

  const ectdParts: string[] = [];
  if (sourceDocument.ectdKey)
    ectdParts.push(`parent key: '${sourceDocument.ectdKey}'`);
  if (sourceDocument.ectdTitle)
    ectdParts.push(`title: '${sourceDocument.ectdTitle}'`);
  const leafInfoLine =
    ectdParts.length > 0
      ? `\nSource document eCTD info: ${ectdParts.join(", ")}`
      : "";

  let combined = `${header}\n\nSource document PDF excerpts:\n\nSource document file name: '${sourceDocument.fileName}'${leafInfoLine}\n\nSource document text: '${sourceDocument.text}'\n\nReference document text: '${reference.text}' `;

  let truncated = !!sourceDocument.inputTruncated;
  if (combined.length > maxChars) {
    combined = `${combined.slice(
      0,
      maxChars
    )}\n\n[Truncated due to input length]`;
    truncated = true;
  }

  const { images } = sourceDocument;
  if (!images || images.length === 0) {
    return { content: combined, truncated };
  }

  const parts: ContentPart[] = [{ type: "text", text: combined }];
  images.forEach((img) => {
    parts.push({
      type: "text",
      text: `\nAttached page image: ${img.label}`,
    });
    parts.push({
      type: "image_url",
      image_url: { url: img.dataUrl },
    });
  });
  return { content: parts, truncated };
}
